import React, { useMemo } from 'react';
import ManCrossPageFooter from '../components/ManCrossPageFooter';

// ── Helpers compartidos ────────────────────────────────────────────────────────

/** Normaliza cualquier clave de dimensión a formato "D{N}" */
const getDimCode = (key) => {
    const s = String(key);
    if (/^D\d+$/i.test(s)) return s.toUpperCase();
    if (/^\d+$/.test(s))   return `D${s}`;
    const m = s.match(/\d+/);
    return m ? `D${m[0]}` : s;
};

/** Top N dimensiones por weight_percent DESC */
const getTopDims = (totals, n = 5) => {
    const avgByDim = (totals || {}).average_by_dimension || {};
    return Object.entries(avgByDim)
        .map(([key, dim]) => {
            const raw   = (dim?.title || key).split('/')[0].trim();
            const words = raw.split(/\s+/).filter(Boolean);

            const codeIdx = words.findIndex(w => /^D\d+$/i.test(w));
            const displayCode = codeIdx >= 0
                ? words[codeIdx].toUpperCase()
                : getDimCode(key);

            const after     = words.slice(codeIdx >= 0 ? codeIdx + 1 : 0);
            const firstWord = after.find(w => /[a-záéíóúüñA-ZÁÉÍÓÚÜÑ]/.test(w)) || '';

            return {
                key,
                displayCode,
                firstWord,
                weightPercent: typeof dim?.weight_percent === 'number' ? dim.weight_percent : 0,
            };
        })
        .sort((a, b) => b.weightPercent - a.weightPercent)
        .slice(0, n);
};

/** Filas de área ordenadas por score ASC (peor primero) */
const buildAreaRows = (totals, byAreaAndDim, topDims) => {
    const areasData = (totals || {}).average_by_management_area || [];
    return areasData
        .map((area) => ({
            name: area.name,
            avg:  typeof area.average === 'number' ? area.average : null,
            dimScores: topDims.reduce((acc, dim) => {
                const score = (byAreaAndDim?.[area.name] || {})[dim.key];
                acc[dim.key] = typeof score === 'number' ? score : null;
                return acc;
            }, {}),
        }))
        .sort((a, b) => (a.avg ?? 999) - (b.avg ?? 999));
};

/** Callout genérico usando los parámetros del config */
const buildDefaultCallout = (areaRows, topDims, { calloutThreshold, deficitLabel, scoreLabel }) => {
    if (!areaRows.length) return '';
    const worst = areaRows[0];
    if (!worst.name) return '';
    const failCount = topDims.filter((d) => {
        const s = worst.dimScores[d.key];
        return s != null && s < calloutThreshold;
    }).length;
    if (failCount === topDims.length) {
        return `${worst.name} falla en las ${failCount} dimensiones de mayor peso — es el área con mayor déficit sistémico de ${deficitLabel}.`;
    }
    const score = worst.avg != null ? Math.round(worst.avg) : '—';
    return `${worst.name} presenta el mayor déficit con ${scoreLabel} ${score} — ${failCount} de ${topDims.length} dimensiones clave bajo el umbral.`;
};

// ── Componente genérico ────────────────────────────────────────────────────────
/**
 * Página de Heatmap por Área reutilizable para IPRA e IASH.
 *
 * Props:
 *   headerText    string  — texto de la barra de encabezado de página
 *   pageNumber    number  — número de página
 *   totals        object  — payload.totals.ipra | payload.totals.iash
 *   byAreaAndDim  object  — payload.ipra_by_area_and_dimension | payload.iash_by_area_and_dimension
 *   config        object  — {
 *     title            string   — "Riesgo por Área"
 *     subtitle         string   — "Mapa de Liderazgo IPRA" | "Mapa de IASH"
 *     promLabel        string   — "IPRA PROM." | "IASH PROM."
 *     scoreLabel       string   — "IPRA" | "IASH" (para texto de callout)
 *     calloutThreshold number   — 75 | 65
 *     deficitLabel     string   — "liderazgo" | "autocuidado"
 *     noteSuffix       string   — "del IPRA total" | "del IASH total"
 *     getCellStyle     fn       — (score) → { bg, text }
 *     getBadgeStyle    fn       — (score) → { bg, text }
 *     buildFooter      fn       — (areaRows, topDims) → string
 *   }
 *
 * Centrado vertical: se usa `.mgcr-hm-body` (flex:1, justify-content:center)
 * en lugar del patrón de dos `keep-blankspace`, que logra el mismo resultado
 * con menos nodos en el árbol DOM.
 */
const ManCrossHeatmapPage = ({
    headerText,
    pageNumber,
    totals,
    byAreaAndDim,
    config,
}) => {
    const {
        title, subtitle, promLabel, scoreLabel,
        calloutThreshold, deficitLabel, noteSuffix,
        getCellStyle, getBadgeStyle, buildFooter,
        getDimHeader,
    } = config;

    const topDims  = useMemo(() => getTopDims(totals),                                            [totals]);
    const areaRows = useMemo(() => buildAreaRows(totals, byAreaAndDim, topDims),                  [totals, byAreaAndDim, topDims]);
    const callout  = useMemo(
        () => buildDefaultCallout(areaRows, topDims, { calloutThreshold, deficitLabel, scoreLabel }),
        [areaRows, topDims, calloutThreshold, deficitLabel, scoreLabel],
    );
    const footer   = useMemo(() => buildFooter(areaRows, topDims), [areaRows, topDims, buildFooter]);

    const top5WeightPct = topDims.reduce((s, d) => s + (d.weightPercent || 0), 0);

    return (
        <div className="mgcr-hm-page">

            {/* Barra de encabezado de página */}
            <div className="mgcr-page-header">
                <span className="mgcr-page-header-text">{headerText}</span>
                <span className="mgcr-page-header-num">{pageNumber}</span>
            </div>

            {/* Cuerpo centrado verticalmente (flex:1 + justify-content:center) */}
            <div className="mgcr-hm-body">

                {/* Título */}
                <div className="mgcr-dx-title-area">
                    <h2 className="mgcr-dx-main-title">{title}</h2>
                    <p  className="mgcr-dx-subtitle">{subtitle}</p>
                </div>

                {/* ── Panel principal ── */}
                <div className="mgcr-hm-panel">

                    {/* Callout header */}
                    <div className="mgcr-hm-callout-row">
                        <div className="mgcr-hm-section-label">Matriz de<br />Dimensiones</div>
                        <div className="mgcr-hm-callout-text">{callout}</div>
                    </div>

                    {/* Nota dimensiones */}
                    <p className="mgcr-hm-note">
                        Se muestran las <strong>5 dimensiones de mayor peso</strong> ({Math.round(top5WeightPct)}% {noteSuffix}).
                    </p>

                    {/* ── Tabla heatmap ── */}
                    <div className="mgcr-hm-table-wrap">

                        {/* Tabla principal: Área + 5 dimensiones */}
                        <div className="mgcr-hm-main-table">

                            {/* Encabezado teal */}
                            <div className="mgcr-hm-row mgcr-hm-header-row">
                                <div className="mgcr-hm-cell mgcr-hm-area-cell mgcr-hm-th-teal">Área</div>
                                {topDims.map((dim) => {
                                    const hdr = getDimHeader
                                        ? getDimHeader(dim)
                                        : { primary: dim.displayCode, secondary: dim.firstWord };
                                    return (
                                        <div key={dim.key} className="mgcr-hm-cell mgcr-hm-dim-cell mgcr-hm-th-teal">
                                            <span className="mgcr-hm-th-code">{hdr.primary}</span>
                                            {hdr.secondary && <span className="mgcr-hm-th-word">{hdr.secondary}</span>}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Filas de datos */}
                            {areaRows.map((row) => (
                                <div key={row.name} className="mgcr-hm-row mgcr-hm-data-row">
                                    <div className="mgcr-hm-cell mgcr-hm-area-cell">{row.name}</div>
                                    {topDims.map((dim) => {
                                        const score = row.dimScores[dim.key];
                                        const cs    = getCellStyle(score);
                                        return (
                                            <div
                                                key={dim.key}
                                                className="mgcr-hm-cell mgcr-hm-dim-cell"
                                                style={{ background: cs.bg, color: cs.text }}
                                            >
                                                {score != null ? Math.round(score) : '—'}
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>

                        {/* Mini-tabla PROM. */}
                        <div className="mgcr-hm-prom-col">

                            {/* Encabezado gris suave */}
                            <div className="mgcr-hm-row mgcr-hm-header-row">
                                <div className="mgcr-hm-cell mgcr-hm-prom-cell mgcr-hm-th-soft">
                                    {promLabel.split(' ').map((w, i) => (
                                        <React.Fragment key={i}>{i > 0 && <br />}{w}</React.Fragment>
                                    ))}
                                </div>
                            </div>

                            {/* Celdas de score */}
                            {areaRows.map((row) => {
                                const badge = getBadgeStyle(row.avg);
                                return (
                                    <div key={row.name} className="mgcr-hm-row mgcr-hm-data-row">
                                        <div
                                            className="mgcr-hm-cell mgcr-hm-prom-cell mgcr-hm-prom-val"
                                            style={{ background: badge.bg, color: badge.text }}
                                        >
                                            {row.avg != null ? Math.round(row.avg) : '—'}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                    </div>{/* /table-wrap */}
                </div>{/* /panel */}

                {/* ── Footer — recuadro separado del panel ── */}
                {footer && (
                    <div className="mgcr-insight-footer">
                        <strong>Esto implica que:</strong>{' '}{footer}
                    </div>
                )}

            </div>{/* /mgcr-hm-body */}

            <ManCrossPageFooter />

        </div>
    );
};

export default ManCrossHeatmapPage;
